// /docs/:docId/review screen. Two-pane layout: ReviewList on the left,
// PreviewPane on the right, PublishBar fixed to the bottom.

import { useState } from "react";
import { useReview, useSaveReview } from "./api";
import { ReviewList } from "./ReviewList";
import { PreviewPane } from "./PreviewPane";
import { PublishBar } from "./PublishBar";
import "./review.css";

interface Props {
  docId: string;
  onPublished?: (publishedAt: string) => void;
}

export function ReviewPage({ docId, onPublished }: Props) {
  const { data, loading, error } = useReview(docId);
  // Wire the debounced save so any store mutation triggers a POST. The
  // PublishBar also uses this hook (their internal refs are independent;
  // the underlying store flag is the source of truth).
  useSaveReview(docId);
  const [previewOpen, setPreviewOpen] = useState(true);

  if (loading) {
    return (
      <div className="maisight-review-loading" role="status">
        Loading review…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="maisight-review-error" role="alert">
        Failed to load review: {error?.message ?? "no data"}
      </div>
    );
  }

  return (
    <div className={`maisight-review-page${previewOpen ? " maisight-review-page--preview-open" : ""}`}>
      <header className="maisight-review-header">
        <div className="maisight-review-eyebrow">Review</div>
        <h1 className="maisight-review-title">{data.doc.title ?? data.doc.doc_id}</h1>
        <p className="maisight-review-sub">
          Accept, edit, or reject each proposed term. Hover a term in the list to see it light up in the preview.
        </p>
      </header>
      <div className="maisight-review-body">
        <div className="maisight-review-pane-left">
          <ReviewList doc={data.doc} />
        </div>
        {previewOpen ? (
          <div className="maisight-review-pane-right">
            <PreviewPane doc={data.doc} />
          </div>
        ) : null}
      </div>
      <PublishBar
        docId={docId}
        onPublished={onPublished}
        onPreview={() => setPreviewOpen((v) => !v)}
      />
    </div>
  );
}
